import React, {useState, useCallback, ChangeEvent} from 'react'
import axios from 'axios'
import {
  IVehicleSaleModel,
  TRANSFER_PROCESS_OPTIONS,
  STATUS_OPTIONS,
  TRANSFER_VALLIDATION,
  DATE_FILTER_OPTIONS,
} from './VehicleSaleModel'
import TextInput from '../../../../shared/components/TextInput' 
import SelectInput from '../../../../shared/components/SelectInput'
import {VehicleSaleFilterModel} from './VehicleSaleFilterModel'

// API'den şube bilgisini çekme
const API_URL = process.env.REACT_APP_API_URL || 'api/v1'
export const BRANCH_GET_ALL_URL = `${API_URL}/definition/branch/all`


interface IOption {
  value: string | number
  label: string
}

export async function branches(): Promise<IOption[]> {
  const res = await axios.get(BRANCH_GET_ALL_URL, {params: {page: 0, size: 100}})
  const data = res.data
  if (!data || !Array.isArray(data.payload?.content)) {
    console.error('branches API response content is not an array:', data.payload?.content)
    return []
  }
  return data.payload.content.map((group: any, index: number) => ({
    value: group.id?.toString() || (index + 1).toString(),
    label: group.branchName || group.toString(),
  }))
}

interface IColumn<T> {
  header: string
  field: keyof T | string
  sortable?: boolean
  render?: (row: T, index?: number) => React.ReactNode
}

interface IDataTableProps<T> {
  data: T[]
  columns: IColumn<T>[]
  currentPage: number
  totalPages: number
  loading: boolean
  sortBy: string
  sortOrder: 'asc' | 'desc'
  onPageChange: (newPage: number) => void
  onSort: (column: string, order: 'asc' | 'desc') => void
  onEdit: (row: T) => void
  onDelete: (row: T) => void
}

function DataTable<T>({
  data,
  columns,
  currentPage,
  totalPages,
  loading,
  sortBy,
  sortOrder,
  onPageChange,
  onSort,
  onEdit,
  onDelete,
}: IDataTableProps<T>): React.ReactElement {
  const renderCell = useCallback((row: T, column: IColumn<T>, index: number) => {
    if (column.render) {
      return column.render(row, index)
    }
    const value = row[column.field as keyof T]
    return value !== null && value !== undefined ? String(value) : ''
  }, [])

  const handleHeaderClick = (column: IColumn<T>) => {
    if (column.sortable) {
      const newOrder = sortBy === column.field && sortOrder === 'asc' ? 'desc' : 'asc'
      onSort(String(column.field), newOrder)
    }
  }



  return (
    <div className='table-responsive'>
      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
        <thead>
          <tr className='fw-bolder text-muted bg-light'>
            {columns.map((column) => (
              <th
                key={String(column.field)}
                className={`min-w-125px ${column.sortable ? 'cursor-pointer' : ''}`}
                onClick={() => handleHeaderClick(column)}
              >
                {column.header}
                {column.sortable && (
                  <span className='ms-1'>
                    {sortBy === column.field ? (
                      sortOrder === 'asc' ? (
                        <i className='fas fa-sort-up'></i>
                      ) : (
                        <i className='fas fa-sort-down'></i>
                      )
                    ) : (
                      <i className='fas fa-sort'></i>
                    )}
                  </span>
                )}
              </th>
            ))}
            <th className='min-w-100px text-end'>İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length + 1} className='text-center'>
                Yükleniyor...
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className='text-center'>
                Veri bulunamadı.
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((column) => (
                  <td key={String(column.field)}>{renderCell(row, column, rowIndex)}</td>
                ))}
                <td className='text-end'>
                  <div className='d-flex justify-content-end flex-shrink-0'>
                    <button
                      type='button'
                      onClick={() => onEdit(row)}
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                      title='Düzenle'
                    >
                      <i className='fas fa-edit'></i>
                    </button>
                    <button
                      type='button'
                      onClick={() => onDelete(row)}
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                      title='Sil'
                    >
                      <i className='fas fa-trash-alt'></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className='d-flex flex-stack flex-wrap pt-10'>
        <div className='fs-6 fw-bold text-gray-700'>Kayıt Sayısı: {data.length}</div>
        <ul className='pagination'>
          <li className={`page-item previous ${currentPage === 0 ? 'disabled' : ''}`}>
            <button
              className='page-link'
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0}
            >
              <i className='previous'></i>
            </button>
          </li>
          {Array.from({length: totalPages}, (_, index) => (
            <li key={index} className={`page-item ${index === currentPage ? 'active' : ''}`}>
              <button className='page-link' onClick={() => onPageChange(index)}>
                {index + 1}
              </button>
            </li>
          ))}
          <li className={`page-item next ${currentPage === totalPages - 1 ? 'disabled' : ''}`}>
            <button
              className='page-link'
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1}
            >
              <i className='next'></i>
            </button>
          </li>
        </ul>
      </div>
    </div>
  )
}

interface IVehicleSaleTableProps {
  data: IVehicleSaleModel[]
  currentPage: number
  totalPages: number
  loading: boolean
  sortBy: string
  sortOrder: 'asc' | 'desc'
  onPageChange: (newPage: number) => void
  onSort: (column: string, order: 'asc' | 'desc') => void
  onEdit: (sale: IVehicleSaleModel) => void
  onDelete: (sale: IVehicleSaleModel) => void
  onFilter: (filters: VehicleSaleFilterModel) => void
}

const columns: IColumn<IVehicleSaleModel>[] = [
  {header: 'Sıra', field: 'index', render: (row, index) => (index !== undefined ? index + 1 : '')},
  {
    header: 'Tarih',
    field: 'saleDate',
    sortable: true,
    render: (sale) => (sale.saleDate ? new Date(sale.saleDate).toLocaleDateString('tr-TR') : ''),
  },
  {
    header: 'Satış Fiyatı',
    field: 'salesPrice',
    sortable: true,
    render: (sale) => `${sale.salesPrice} TL`,
  },
  {header: 'Devir İşlemi', field: 'transferOperation', sortable: true},
  {header: 'Plaka', field: 'vehiclePlate', sortable: false},
  {header: 'Marka', field: 'brand', sortable: true},
  {header: 'Tip', field: 'modelType', sortable: true},
  {header: 'Yakıt Türü', field: 'fuelType', sortable: true},
  {header: 'Durum', field: 'status', sortable: true},
]

export const VehicleSaleTable: React.FC<IVehicleSaleTableProps> = ({
  data,
  currentPage,
  totalPages,
  loading,
  sortBy,
  sortOrder,
  onPageChange,
  onSort,
  onEdit,
  onDelete,
  onFilter,
}) => {
  const [filters, setFilters] = useState<VehicleSaleFilterModel>({
    recordNumber: '',
    date: '',
    sold: false,
    transferOperation: false,
    vehiclePlate: '',
    brand: '',
    modelType: '',
    fuelType: '',
    modelYear: 0,
    transmission: '',
    color: '',
    minKilometer: 0,
    maxKilometer: 0,
    branch: '',
    minElapsedDays: 0,
    maxElapsedDays: 0,
    buyer: '',
    vehicleOwner: '',
    minSecondHandPrice: 0,
    maxSecondHandPrice: 0,
    minListPrice: 0,
    maxListPrice: 0,
    saleStartDate: '',
    saleEndDate: '',
    minSalePrice: 0,
    maxSalePrice: 0,
    minPurchasePrice: 0,
    maxPurchasePrice: 0,
    engineNumber: '',
    chassisNumber: '',
    bodyType: '',
    registrationStartDate: '',
    registrationEndDate: '',
    targetPrice: 0,
    auctionCompany: '',
    auctionStartDate: '',
    auctionEndDate: '',
    minCascoAmount: 0,
    maxCascoAmount: 0,
    minTrafficInsuranceAmount: 0,
    maxTrafficInsuranceAmount: 0,
  })

  const loadBranchesOptions = useCallback(async (query: string): Promise<IOption[]> => {
    try {
      const branchOptions = await branches()
      return branchOptions.filter(
        (option) =>
          typeof option.label === 'string' &&
          option.label.toLowerCase().includes(query.toLowerCase())
      )
    } catch (error) {
      console.error('Şube verileri yüklenirken hata oluştu:', error)
      return []
    }
  }, [])

  const handleFilterChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {name, value, type} = event.target
    let formattedValue: any = value

    if (type === 'checkbox') {
      formattedValue = (event.target as HTMLInputElement).checked
    } else if (type === 'number') {
      formattedValue = Number(value)
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: formattedValue,
    }))
  }

  const handleBranchChange = (selectedOption: IOption | null) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      branch: selectedOption ? String(selectedOption.value) : '',
    }))
  }

  const handleFilter = () => {
    onFilter(filters)
  }
    const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])

  return (
    <>
      <div className='card-body pt-0'>
        {/* Satış Listesi Filtreleme Alanı */}
        <div className='row mb-5'>
          {/* Sol Kısım */}
          <div className='col-md-6'>
            {/* Tarih Aralığı */}
            <div className='row mb-3 align-items-end'>
              <SelectInput
                name='dateFilterType'
                label='Tarih Türü'
                options={DATE_FILTER_OPTIONS}
                wrapperClassName='col-md-4'
              />
              <TextInput
                name='saleStartDate'
                label='Başlangıç'
                type='date'
                value={filters.saleStartDate || ''}
                onChange={handleFilterChange}
                wrapperClassName='col-md-4'
              />
              <TextInput
                name='saleEndDate'
                label='Bitiş'
                type='date'
                value={filters.saleEndDate || ''}
                onChange={handleFilterChange}
                wrapperClassName='col-md-4'
              />
            </div>

            {/* Devir İşlemleri */}
            <div className='row mb-3 align-items-end'>
              <SelectInput
                name='sold'
                label='Devir'
                options={TRANSFER_VALLIDATION}
                wrapperClassName='col-md-4'
              />
              <SelectInput
                name='transferProcess'
                label='İşlem'
                options={TRANSFER_PROCESS_OPTIONS}
                wrapperClassName='col-md-4'
              />
              <TextInput
                name='transferOperation'
                label='Miktar'
                type='number'
                value={filters.transmission || ''}
                onChange={handleFilterChange}
                wrapperClassName='col-md-4'
              />
            </div>

            {/* Durum */}
            <div className='row mb-3'>
              <div className='col'>
                <SelectInput
                  name='status'
                  label='Durum'
                  options={STATUS_OPTIONS}
                  wrapperClassName='w-100'
                />
              </div>
            </div>
          </div>

          {/* Sağ Kısım */}
          <div className='col-md-6'>
            {/* Plaka */}
            <div className='row mb-3'>
              <div className='col'>
                <TextInput
                  name='vehiclePlate'
                  label='Araç Plaka'
                  placeholder='Plaka Giriniz'
                  value={filters.vehiclePlate || ''}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            {/* Şube */}
            <div className='row mb-3'>
              <div className='col'>
                <SelectInput
                  name='branch'
                  label='Şube Seç'
                  wrapperClassName='col-md-12'
                  options={branchOptions}
                />
              </div>
            </div>
            {/* Boş alan ve Butonlar */}
            <div className='row mb-3'>
              <div className='col d-flex align-items-end justify-content-end gap-3'>
                <button onClick={handleFilter} className='btn btn-primary'>
                  <i className='bi bi-funnel-fill me-2'></i>Filtrele
                </button>
                <span className='text-muted fw-bold'>Kayıt Sayısı: {data.length}</span>
              </div>
            </div>
          </div>
        </div>
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          currentPage={currentPage}
          totalPages={totalPages}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onPageChange={onPageChange}
          onEdit={onEdit}
          onDelete={onDelete}
          onSort={onSort}
        />
      </div>
    </>
  )
}
